<template>
  <div class="p-4 w-48.75 self-stretch flex flex-col rounded-3xl bg-key">
    <div class="flex shrink-0 items-center gap-1 h-5.5">
      <Icon
        icon="Technology-Wireframe-KeyBottomDZ"
        class="size-5"
        :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
      />
      <span
        class="text-m-medium text-black-white"
        :class="`${disabled ? 'text-text-disabled' : 'text-black-white'}`"
        >{{ t('itinerary.bottomDeadZone.title') }}</span
      >
    </div>
    <div class="min-h-10 shrink-0">
      <p
        class="text-s-medium"
        :class="`${disabled ? 'text-text-disabled' : 'text-text-glass-default'}`"
      >
        {{ t('itinerary.bottomDeadZone.description') }}
      </p>
    </div>
    <div class="flex-1 flex gap-1">
      <div class="h-full flex flex-col justify-between">
        <Button
          color="text-b-w"
          variant="frameless"
          shape="square"
          size="icon-xs"
          :disabled="disabled"
          @click="handleStepUp"
        >
          <Icon icon="Direction-Wireframe-Up2-1" :class="{ 'text-text-disabled': disabled }" />
        </Button>
        <Button
          color="text-b-w"
          variant="frameless"
          shape="square"
          size="icon-xs"
          :disabled="disabled"
          @click="handleStepDown"
        >
          <Icon icon="Direction-Wireframe-Down2" :class="{ 'text-text-disabled': disabled }" />
        </Button>
      </div>
      <div class="relative" :class="{ 'opacity-25': disabled }">
        <Slider
          v-model="currentValue"
          :min="0"
          :max="0.69"
          :step="0.01"
          :disabled="disabled"
          direction="vertical"
          thumb-alignment="overflow"
          class="h-34 w-22.5"
          track-class="w-full rounded-lg bg-linear-[var(--color-rt1)]"
          range-class="bg-transparent"
          thumb-class="bg-transparent w-full h-3.5 rounded-none"
          @value-commit="commitDeadZone"
        >
          <template #thumb>
            <div class="relative flex justify-center">
              <Icon icon="Direction-FIll-SwipeUD1" class="w-3.5 h-3.5 text-black-white" />
              <div class="absolute w-full h-px bg-black-white top-[50%]" />

              <div
                class="absolute -right-19 top-1/2 -translate-y-1/2 pb-0.5 -translate-x-1/2 pointer-events-none text-center leading-none"
              >
                <span class="text-s-medium text-black-white">
                  {{ currentValue[0].toFixed(2) }}mm
                </span>
              </div>
            </div>
          </template>
        </Slider>

        <div
          class="absolute top-1 left-1 right-1 text-s-medium text-blue-dark-default pointer-events-none"
        >
          0.69mm
        </div>
        <div
          class="absolute bottom-1 left-1 right-1 text-s-medium text-brand-pink pointer-events-none"
        >
          0mm
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { Button, Icon, Slider } from '@rapoo/element'
  import { useI18n } from 'vue-i18n'

  const props = defineProps<{
    bottomDeadZone: number
    deadZoneEnabled: boolean
  }>()

  const emit = defineEmits<{
    'update:bottomDeadZone': [value: number]
  }>()

  const { t } = useI18n()

  const currentValue = ref([props.bottomDeadZone / 100])
  watch(
    () => props.bottomDeadZone,
    (val) => {
      currentValue.value = [val / 100]
    },
  )

  const commitDeadZone = (val: number[]) => {
    emit('update:bottomDeadZone', Math.round(val[0] * 100))
  }

  const disabled = computed(() => !props.deadZoneEnabled)

  const handleStepUp = () => {
    if (disabled.value) return
    emit('update:bottomDeadZone', Math.min(69, props.bottomDeadZone + 1))
  }

  const handleStepDown = () => {
    if (disabled.value) return
    emit('update:bottomDeadZone', Math.max(0, props.bottomDeadZone - 1))
  }
</script>

<style scoped></style>
